// @flow
import URI from 'urijs';

import { get, isEmpty } from 'lodash';
import config from 'Config';
import { oidc as oidcUtils, windowWrapper } from '@clubmed/utils';
import * as routeBuilder from 'Routes/routeBuilder';
import { clearAllCacheFront } from 'Utils/graphql-client/cacheFront';

import { getClientID as getVisitorGUID } from 'Tracking/google-analytics/googleAnalytics';
export const QUICK_LOGIN_QUERY_PARAM = 'bookingGM';

export function isCxoSellerContext(queryParameters: string): boolean {
  return queryParameters ? queryParameters.includes('cxo_neolid=') : false;
}

function getRedirectUri(redirectUri?: string) {
  const pathAccount = routeBuilder.buildUrl('account');

  if (redirectUri) return redirectUri;
  return typeof window !== 'undefined'
    ? new URI(windowWrapper.getOrigin()).path(pathAccount).toString()
    : pathAccount;
}

function getHomeUri() {
  const pathAccount = routeBuilder.buildUrl('home');

  return typeof window !== 'undefined'
    ? new URI(windowWrapper.getOrigin()).path(pathAccount).toString()
    : pathAccount;
}

function getRedirectUriError(redirectURIError?: string) {
  const pathError = routeBuilder.buildUrl('error');

  if (redirectURIError) return redirectURIError;
  return typeof window !== 'undefined'
    ? new URI(windowWrapper.getOrigin()).path(pathError).toString()
    : pathError;
}

function getLocaleQueryParams(redirectUri?: URI): { locale?: string } {
  let redirectUriQueryParameters;
  if (typeof window !== 'undefined') {
    redirectUriQueryParameters = URI.parseQuery(windowWrapper.getLocationPart('search'));
  } else if (redirectUri) {
    redirectUriQueryParameters = URI.parseQuery(redirectUri.query());
  }

  const locale = get(redirectUriQueryParameters, 'locale');
  return locale ? { locale: locale } : {};
}

type queriesProps = {
  locale: string,
  redirectUri?: string,
  redirectURIError?: string,
  state?: string,
  provider?: string,
  isSeller?: boolean,
  isCmta?: boolean,
  isQuickLoginFirst?: boolean,
};

function getOidcQuery({
  locale,
  redirectUri,
  redirectURIError,
  state,
  provider,
  isSeller = false,
  isQuickLoginFirst = false,
}: queriesProps) {
  return URI.buildQuery({
    client_id: isSeller ? config.get('oidc.seller.client_id') : config.get('oidc.client_id'),
    redirect_uri: isSeller
      ? config.get('oidc.seller.proxy.signin_redirect_uri')
      : config.get('oidc.proxy.signin_redirect_uri'),
    response_type: config.get('oidc.response_type'),
    state: oidcUtils.toUriEncodedState({
      redirect_uri: getRedirectUri(redirectUri),
      home_uri: getHomeUri(),
      redirect_uri_on_error: getRedirectUriError(redirectURIError),
      state: oidcUtils.toUriEncodedState(state),
    }),
    scope: config.get('oidc.scope'),
    locale,
    provider: provider || (isQuickLoginFirst ? QUICK_LOGIN_QUERY_PARAM : undefined),
    visitorGUID: getVisitorGUID(),
  });
}

export function buildSigninUrl({
  locale,
  redirectUri,
  redirectURIError,
  state,
  provider,
  isQuickLoginFirst,
}: queriesProps) {
  return new URI(config.get('oidc.host'))
    .path(config.get('oidc.authorize'))
    .query(
      getOidcQuery({ locale, redirectUri, redirectURIError, state, provider, isQuickLoginFirst }),
    )
    .toString();
}

export function buildChangePassword({ redirectUri, locale }: queriesProps) {
  return new URI(config.get('oidc.host'))
    .path(config.get('oidc.changePassword'))
    .query(getOidcQuery({ redirectUri, locale }))
    .toString();
}

export function buildFacebookConnectUrl({ locale, redirectUri, state }: queriesProps) {
  return new URI(config.get('oidc.host'))
    .path(config.get('oidc.facebookConnect'))
    .query(getOidcQuery({ locale, redirectUri, state }))
    .toString();
}

export function buildStepupUrl({ locale, state, redirectURIError, redirectUri }: queriesProps) {
  return new URI(config.get('oidc.host'))
    .path(config.get('oidc.stepup'))
    .query(getOidcQuery({ locale, redirectUri, redirectURIError, state }))
    .toString();
}

export function buildWechatConnectUrl({ locale, redirectUri, state }: queriesProps) {
  return new URI(config.get('oidc.host'))
    .path(config.get('oidc.wechatConnect'))
    .query(getOidcQuery({ locale, redirectUri, state }))
    .toString();
}

export function buildSellerSigninUrl({ redirectUri, locale, isCmta }: queriesProps) {
  return new URI(config.get('oidc.seller.host'))
    .path(isCmta ? config.get('oidc.seller.partner_login') : config.get('oidc.seller.authorize'))
    .query(getOidcQuery({ redirectUri, isSeller: true, locale }))
    .toString();
}

export function buildSignupUrl({ redirectUri, locale }: queriesProps) {
  return new URI(config.get('oidc.host'))
    .path(config.get('oidc.signup'))
    .query(getOidcQuery({ redirectUri, locale }))
    .toString();
}

export function buildSignoutUrl(
  visitorGUIDDisabled: boolean = false,
  redirectPath?: string,
  isSeller?: boolean,
) {
  if (typeof window !== 'undefined') {
    const guid = getVisitorGUID();
    const visitorGUID = !visitorGUIDDisabled && guid ? { visitorGUID: guid } : {};
    const queryParameters = {
      ...getLocaleQueryParams(),
      ...visitorGUID,
      isSeller,
    };

    if (redirectPath) {
      const redirectUri = new URI(windowWrapper.getOrigin())
        .path(redirectPath)
        .query(windowWrapper.getLocationPart('search'))
        .hash(window.location.hash)
        .toString();

      // $FlowFixMe
      queryParameters.redirectUri = redirectUri;
    }

    const url = routeBuilder.buildUrl(
      'signout',
      {},
      !isEmpty(queryParameters) ? queryParameters : null,
    );
    return url;
  }
  return null;
}

export function buildErrorUrl(currentUri: string, locale: string): string {
  return new URI(currentUri)
    .path(routeBuilder.buildUrl('error'))
    .query({
      lang: locale,
    })
    .hash('')
    .toString();
}

export function redirectOnLoginPage({
  locale,
  redirectPath,
  state,
  isSeller,
  isQuickLoginFirst,
}: {
  locale: string,
  redirectPath: string,
  state?: string,
  isSeller?: boolean,
  isQuickLoginFirst?: boolean,
}) {
  if (typeof window !== 'undefined') {
    const redirectUri = new URI(windowWrapper.getOrigin())
      .path(redirectPath)
      .query(windowWrapper.getLocationPart('search'))
      .hash(windowWrapper.getLocationPart('hash'))
      .toString();

    clearAllCacheFront();
    windowWrapper.setLocation(
      isSeller || isCxoSellerContext(windowWrapper.getLocationPart('search'))
        ? buildSellerSigninUrl({ locale, redirectUri })
        : buildSigninUrl({ locale, redirectUri, state, isQuickLoginFirst }),
    );
  }
}

function hasStatus(httpResponse: *, statusCode: number) {
  const errors = get(httpResponse, 'errors', []) || [];
  return Array.isArray(errors) ? errors.some(err => err.statusCode === statusCode) : false;
}

export function hasAuthResponseError(httpResponse: *) {
  return hasStatus(httpResponse, 401);
}

export function hasNotFoundError(httpResponse: *) {
  return hasStatus(httpResponse, 404);
}

export function redirectToOidc(locale: string) {
  redirectOnLoginPage({
    redirectPath: new URI(windowWrapper.getLocation()).pathname(),
    locale,
  });
}

export function createDocumentUrl({
  neolaneId,
  userBooking,
  file,
  locale,
  origin,
}: {
  neolaneId: ?string,
  userBooking: Booking,
  file: { id: string },
  locale: string,
  origin: string,
}) {
  const gqlHost = config.get('graphql.host');
  // TODO use buildErrorUrl
  const redirectUrlError = `${new URI(origin).origin()}/error`;
  return `${gqlHost}${
    gqlHost && gqlHost.charAt(gqlHost.length - 1) === '/' ? '' : '/'
  }documents/${String(neolaneId)}/${get(userBooking, 'information.bookingId')}/${
    file.id
  }?redirect_url_error=${redirectUrlError}&locale=${locale}`;
}
